import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Button, Card, DatePicker, Flex, Space, Typography } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import WeeklyMeetingGrid from "../../components/hrm/WeeklyMeetingGrid";
import { Status } from "../../enum/common";
import { IMeetingSchedule } from "../../models/Hrm";
import {
  useGetAllMeetingSchedule,
  useReviewMeetingSchedule,
} from "../../query/HrmQuery";
import { notify } from "../../utils/toast";

const { Text } = Typography;

const getStartOfWeek = (date: Dayjs) => {
  const day = date.day();
  const diff = day === 0 ? -6 : 1 - day;
  return date.add(diff, "day").startOf("day");
};

const HrmMeetingSchedule: React.FC = () => {
  const navigate = useNavigate();

  const [currentWeekStart, setCurrentWeekStart] = useState<Dayjs>(
    getStartOfWeek(dayjs()),
  );

  const [time, setTime] = useState<Date>(currentWeekStart.toDate());

  useEffect(() => {
    setTime(currentWeekStart.toDate());
  }, [currentWeekStart]);

  const { data: schedulesData, isLoading: schedulesLoading } =
    useGetAllMeetingSchedule(time.toISOString());
  const schedules = schedulesData;
  const mutationReview = useReviewMeetingSchedule();

  const handleReview = async (status: Status, id: number) => {
    try {
      await mutationReview.mutateAsync({
        id,
        data: status,
      });
      notify.success("Duyệt lịch họp thành công");
    } catch (err: unknown) {
      notify.error("Duyệt lịch họp thất bại");
    }
  };

  const getListData = (value: Dayjs) => {
    return (
      schedules?.filter((schedule: IMeetingSchedule) =>
        dayjs(schedule.startTime).isSame(value, "day"),
      ) || []
    );
  };

  const weekStartMonth = currentWeekStart.format("MM/YYYY");
  const weekEndMonth = currentWeekStart.add(6, "day").format("MM/YYYY");
  const monthDisplay =
    weekStartMonth === weekEndMonth
      ? `Tháng ${weekStartMonth}`
      : `Tháng ${weekStartMonth} - ${weekEndMonth}`;

  return (
    <Flex vertical gap="middle" className="p-4 w-full overflow-hidden">
      <Flex justify="end">
        <Space>
          <Button onClick={() => navigate("/hrm/meeting-rooms")}>
            Danh sách phòng họp
          </Button>
        </Space>
      </Flex>
      <Card
        loading={schedulesLoading}
        className="w-full overflow-hidden"
        styles={{ body: { padding: 16, overflowX: "auto" } }}
        title={
          <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
            <Text strong className="text-base md:text-lg">
              Lịch họp toàn công ty theo tuần - {monthDisplay} (
              {schedules?.length || 0})
            </Text>
            <Space wrap>
              <DatePicker
                picker="week"
                value={currentWeekStart}
                allowClear={false}
                placeholder="Chọn tuần"
                onChange={(date) => {
                  if (date) setCurrentWeekStart(getStartOfWeek(date));
                }}
              />
              <Button
                icon={<LeftOutlined />}
                onClick={() =>
                  setCurrentWeekStart((prev) => prev.subtract(1, "week"))
                }
              />
              <Button
                onClick={() => setCurrentWeekStart(getStartOfWeek(dayjs()))}
              >
                Tuần này
              </Button>
              <Button
                icon={<RightOutlined />}
                onClick={() =>
                  setCurrentWeekStart((prev) => prev.add(1, "week"))
                }
              />
            </Space>
          </Flex>
        }
      >
        <WeeklyMeetingGrid
          currentWeekStart={currentWeekStart}
          getListData={getListData}
          isHrmView={true}
          onReview={handleReview}
        />
      </Card>
    </Flex>
  );
};

export default HrmMeetingSchedule;
